/*
 This file is part of GNU Taler
 (C) 2026 Taler Systems S.A.

 GNU Taler is free software; you can redistribute it and/or modify it under the
 terms of the GNU General Public License as published by the Free Software
 Foundation; either version 3, or (at your option) any later version.

 GNU Taler is distributed in the hope that it will be useful, but WITHOUT ANY
 WARRANTY; without even the implied warranty of MERCHANTABILITY or FITNESS FOR
 A PARTICULAR PURPOSE.  See the GNU General Public License for more details.

 You should have received a copy of the GNU General Public License along with
 GNU Taler; see the file COPYING.  If not, see <http://www.gnu.org/licenses/>
 */

import type { VNode } from "preact";
import { useCallback, useState } from "preact/hooks";
import { useOrders } from "../api/hooks.js";
import { OrderListScreen, type OrderListScreenProps } from "../screens/OrderListScreen.js";

interface OrderQuery {
  delta: number;
  status: string;
  search: string;
  offset?: string;
}

export function OrderListRoute(props: OrderListScreenProps): VNode {
  // Tab, search and paging all live on the screen and all narrow one query, so
  // the screen reports them together and the route re-fetches.
  const [query, setQuery] = useState<OrderQuery>({ delta: 21, status: "all", search: "" });

  const { orders, isLoading, isValidating, lastUpdated, error, liveError, mutate, resource } = useOrders(query);

  // Stable, so the screen's effect does not re-run on every render.
  const onQueryChange = useCallback((next: OrderQuery) => {
    setQuery((prev) =>
      prev.delta === next.delta &&
      prev.status === next.status &&
      prev.search === next.search &&
      prev.offset === next.offset
        ? prev
        : next,
    );
  }, []);

  return (
    <OrderListScreen
      {...props}
      liveOrders={orders}
      liveLoading={isLoading}
      liveValidating={isValidating}
      liveLastUpdated={lastUpdated}
      liveError={error}
      liveUpdateError={liveError}
      ordersResource={resource}
      onRefresh={mutate}
      onQueryChange={onQueryChange}
    />
  );
}
